(() => {
  'use strict';

  // One Shopify-native Aurora product-card per colour, never a simulated gallery.
  // Keep the original card and linked colour selector mounted in the same grid slot.
  const states = new WeakMap();
  let nextSlotId = 0;

  function stateFor(link) {
    const slot = link.closest('.shop__grid-item');
    if (!slot) return null;
    let state = states.get(slot);
    // Collection filtering may replace cards without replacing their grid slots.
    if (state && slot.querySelector('product-card') !== state.activeCard) {
      states.delete(slot);
      state = null;
    }
    if (state) return state;

    const originalCard = slot.querySelector('product-card');
    const swatches = originalCard?.querySelector('[data-plus351-collection-colours]');
    const originalOption = swatches?.querySelector('[data-plus351-original]');
    const originalURL = originalOption?.getAttribute('href');
    if (!originalCard || !swatches || !originalURL) return null;

    // Store a disconnected template, not a previously connected native component.
    // Every colour selection then instantiates an untouched product-card.
    const baseTemplate = originalCard.cloneNode(true);
    baseTemplate.querySelector('[data-plus351-collection-colours]')?.remove();

    state = {
      slot,
      uid: ++nextSlotId,
      activeCard: originalCard,
      swatches,
      originalURL,
      selectedURL: originalURL,
      cards: new Map([[originalURL, baseTemplate]]),
      pending: new Map(),
      request: 0
    };
    states.set(slot, state);
    return state;
  }

  function setSelected(state, url) {
    state.swatches.querySelectorAll('a[data-plus351-collection-colour]').forEach((link) => {
      if (link.getAttribute('href') === url) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    state.selectedURL = url;
  }

  function sectionURL(state, link) {
    const raw = link.getAttribute('href');
    if (!raw) return null;
    const destination = new URL(raw, window.location.href);
    if (destination.origin !== window.location.origin) return null;

    destination.hash = '';
    destination.searchParams.set('section_id', 'plus351-linked-native-card');

    // A Draft preview must fetch the Draft's section rather than the live theme.
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) destination.searchParams.set('preview_theme_id', preview);
    return destination.href;
  }

  function scopeCarouselId(card, uid) {
    // Different collection cards may link to the same product. Give every new
    // native carousel a unique ID before its web component connects.
    card.querySelectorAll('carousel-component[id]').forEach((carousel) => {
      const oldId = carousel.id;
      if (!oldId || !oldId.startsWith('product-card__')) return;
      const unique = oldId + '--linked-' + uid;
      carousel.id = unique;
      card.querySelectorAll('[data-target-selector]').forEach((dots) => {
        if (dots.getAttribute('data-target-selector') === '#' + oldId) {
          dots.setAttribute('data-target-selector', '#' + unique);
        }
      });
    });
  }

  function loadCard(state, link) {
    const href = link.getAttribute('href');
    if (state.cards.has(href)) return Promise.resolve(state.cards.get(href));
    if (state.pending.has(href)) return state.pending.get(href);

    const url = sectionURL(state, link);
    if (!url) return Promise.reject(new Error('Invalid linked product URL'));

    const task = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('Shopify returned ' + response.status);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fragment = parsed.querySelector('[data-plus351-native-card-fragment]');
        const card = fragment?.querySelector('product-card');
        const expectedId = link.getAttribute('data-plus351-product-id');

        if (!card || (expectedId && card.getAttribute('data-id') !== expectedId)) {
          throw new Error('Incorrect or missing native product card');
        }
        // Adopt into the storefront Document so Aurora's custom elements
        // initialize normally when the card is actually inserted.
        const adopted = document.importNode(card, true);
        if (!adopted.querySelector('.product-card__price')) {
          throw new Error('Native product card has no price container');
        }
        // Keep an inert template; a fresh native element will connect on each
        // activation, preventing duplicate listeners across detach/reconnect.
        // Warm the featured photo in the browser cache before the first swap.
        const featured = adopted.querySelector('img[data-product-card-featured-image]');
        const src = featured?.getAttribute('src');
        if (src) {
          const preloader = new Image();
          preloader.decoding = 'async';
          preloader.src = src;
        }
        state.cards.set(href, adopted);
        return adopted;
      })
      .finally(() => state.pending.delete(href));

    state.pending.set(href, task);
    return task;
  }

  function activate(state, template, href) {
    if (!state.activeCard?.isConnected) return;
    const target = template.cloneNode(true);
    const price = target.querySelector('.product-card__price');
    // In the original product-card Liquid, colour swatches are a sibling of
    // .product-card__price inside .product-card__info, not a child of price.
    if (!price || !price.parentElement?.classList.contains('product-card__info')) return;

    // Keep collection-specific product image proportions when rendering a
    // linked card from the product page's static section.
    const currentShape = state.activeCard.querySelector('[data-product-card-inner]');
    const nextShape = target.querySelector('[data-product-card-inner]');
    if (currentShape && nextShape) {
      const shapeTokens = currentShape.className.split(/\s+/).filter((token) => token.startsWith('shape--'));
      const originalTokens = nextShape.className.split(/\s+/).filter((token) => !token.startsWith('shape--'));
      nextShape.className = [...originalTokens, ...shapeTokens].join(' ');
    }

    scopeCarouselId(target, state.uid);

    // The ONLY custom part is the linked colour selection UI. Everything
    // else — Carousel, Media Tabs, dots and cart — is native Aurora.
    price.after(state.swatches);
    state.activeCard.replaceWith(target);
    state.activeCard = target;
    setSelected(state, href);
    state.swatches.removeAttribute('aria-busy');
  }

  // A desktop hover or keyboard focus starts the exact same native-card request
  // ahead of the eventual click. The per-colour promise cache deduplicates it.
  function prefetchNativeColour(event) {
    if (event.type === 'pointerover' && event.pointerType === 'touch') return;
    const link = event.target.closest?.('a[data-plus351-collection-colour]');
    if (!link || link.hasAttribute('data-plus351-original')) return;
    const state = stateFor(link);
    if (!state) return;
    const href = link.getAttribute('href');
    if (!href || state.cards.has(href) || state.pending.has(href)) return;
    loadCard(state, link).catch(() => {
      // No navigation on speculative prefetch failure; clicking still falls back.
    });
  }
  document.addEventListener('pointerover', prefetchNativeColour, true);
  document.addEventListener('focusin', prefetchNativeColour, true);

  document.addEventListener('click', (event) => {
    const option = event.target.closest('a[data-plus351-collection-colour]');
    if (!option) return;

    const state = stateFor(option);
    if (!state) return;
    const href = option.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    event.stopPropagation();

    if (href === state.selectedURL) {
      // Clicking the still-selected colour while another is loading cancels
      // that selection; its eventual network response must never override it.
      if (state.swatches.hasAttribute('aria-busy')) {
        state.request += 1;
        state.swatches.removeAttribute('aria-busy');
      }
      return;
    }
    const request = ++state.request;
    const original = href === state.originalURL;
    if (original) {
      activate(state, state.cards.get(state.originalURL), href);
      return;
    }

    state.swatches.setAttribute('aria-busy', 'true');
    loadCard(state, option).then((card) => {
      if (request !== state.request) return;
      activate(state, card, href);
    }).catch((error) => {
      if (request !== state.request) return;
      state.swatches.removeAttribute('aria-busy');
      console.warn('[+351] Native colour card could not load:', error);
      // Preserve the ability to reach the chosen product if section rendering fails.
      window.location.assign(href);
    });
  }, true);
})();
